import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ErrorService } from '@mkp/core/feature-errors';
import { selectIsDefault } from '@mkp/debug/state';
import { LanguageIso } from '@mkp/shared/models';
import { Store } from '@ngrx/store';
import { selectInterfaceLanguage } from '@mkp/core/state';
import { EMPTY, Observable, concat, filter, interval, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, map, switchMap, take, tap } from 'rxjs/operators';

const DEFAULT_MAX_RETRY = 3;
const NB_SHORT_POLLS = 3;
const SHORT_POLL_INTERVAL = 500;
const LONG_POLL_INTERVAL = 3000;

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);
  private readonly errorService = inject(ErrorService);

  private authExceptionURLs = new Map<number, string[]>([
    [304, ['/company-profile']],
    [400, ['/check-email']],
    [401, ['/api/v2/shop', '/account', '/insight']],
    [403, ['/api/account', '/company-profile']],
    [404, ['/user', '/check-is-claimed', '/check-email']],
    [500, ['/commercial-register-entry', '/uid-register-entry', '/openai']],
  ]);

  intercept(req: HttpRequest<Request>, next: HttpHandler): Observable<HttpEvent<Response>> {
    const maxRetry = req.headers.has('maxRetry')
      ? Number(req.headers.get('maxRetry'))
      : DEFAULT_MAX_RETRY;
    let nbAttempt = 0;

    return this.getPollingClock(maxRetry).pipe(
      // we use concatMap to queue requests one after another
      concatMap(() =>
        of(req).pipe(
          tap(() => (nbAttempt += 1)),
          map((request) => this.buildRequest(request)),
          switchMap((request) => next.handle(request)),
          // we need to swallow errors in the substream until we reach the last attempt
          catchError((err: unknown) => (nbAttempt >= maxRetry ? throwError(() => err) : EMPTY))
        )
      ),
      // if one request succeeds : complete the observable
      take(1),
      catchError((error: unknown) => this.requestError(error as HttpErrorResponse))
    );
  }

  private buildRequest(req: HttpRequest<Request>): HttpRequest<Request> {
    return this.requestResponseType(this.requestHeaders(req.clone()));
  }

  private requestHeaders(cloneReq: HttpRequest<Request>): HttpRequest<Request> {
    return cloneReq.clone({ headers: this.buildHeaders(cloneReq) });
  }

  private buildHeaders(cloneReq: HttpRequest<Request>): HttpHeaders {
    const url = cloneReq.url;

    const cloneReqHeaders = cloneReq.headers
      .keys()
      .filter((k) => k !== 'maxRetry')
      .reduce((acc, key) => {
        acc[key] = cloneReq.headers.get(key);
        return acc;
      }, {} as Headers);

    const acceptLanguage = url.indexOf('meta') > -1 || url.indexOf('ats') > -1;
    const wildcard = url.indexOf('skill') > -1 || url.indexOf('job-title') > -1;

    // request-supplied headers take precedence over the derived Accept-Language
    const headers = acceptLanguage
      ? { 'Accept-Language': wildcard ? '*' : this.getAppLanguage(), ...cloneReqHeaders }
      : cloneReqHeaders;

    return new HttpHeaders(headers);
  }

  private getAppLanguage(): LanguageIso {
    let appLanguage = LanguageIso.ENGLISH;
    this.store
      .select(selectInterfaceLanguage)
      .pipe(take(1))
      .subscribe((language) => {
        appLanguage = language;
      });

    return appLanguage;
  }

  private requestResponseType(cloneReq: HttpRequest<Request>): HttpRequest<Request> {
    return cloneReq.url.indexOf('change_password') > -1
      ? cloneReq.clone({ responseType: 'text' })
      : cloneReq;
  }

  private requestError(err: HttpErrorResponse): Observable<never> {
    this.log(err);

    const isException = this.checkIfErrorIsException(err);
    // if error is an exception : throw error. Otherwise add the error to the error stack and continue as if nothing happened
    return isException ? throwError(() => err) : this.errorService.handleError(err);
  }

  private log(err: HttpErrorResponse) {
    this.store
      .select(selectIsDefault)
      .pipe(
        take(1),
        filter((isDefault) => isDefault),
        tap(() => {
          console.log('<> requestError', err);
        })
      )
      .subscribe();
  }

  private checkIfErrorIsException(err: HttpErrorResponse): boolean {
    const exceptionUrls = this.authExceptionURLs.get(err.status);
    return exceptionUrls
      ? exceptionUrls.some((exceptionUrl) => err.url.includes(exceptionUrl))
      : false;
  }

  // we run x short polls, then y long polls so that x + y = maxRetry
  private getPollingClock(maxRetry: number): Observable<number> {
    return concat(
      timer(0, SHORT_POLL_INTERVAL).pipe(take(Math.min(NB_SHORT_POLLS, maxRetry))),
      interval(LONG_POLL_INTERVAL).pipe(take(Math.max(0, maxRetry - NB_SHORT_POLLS)))
    );
  }
}
